import React, { useEffect, useState } from "react";
import { Button, Form, Alert, Badge } from "react-bootstrap";
import { listLeads, submitQuote, listMessages, sendMessage } from "../services/partnerAuthService";

const getErr = (err, fallback) => err.response?.data?.error || fallback;
const emptyItem = () => ({ label: "", amount: "" });

function ChatPanel({ lead, onClose }) {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");

  const load = () => {
    listMessages(lead._id)
      .then(({ data }) => setMessages(data.data || []))
      .catch((err) => setError(getErr(err, "Failed to load messages")))
      .finally(() => setLoading(false));
  };

  useEffect(load, [lead._id]);

  const handleSend = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    setError("");
    try {
      await sendMessage(lead._id, text.trim());
      setText("");
      load();
    } catch (err) {
      setError(getErr(err, "Unable to send message"));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="partner-card mt-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <h6 className="mb-0">Chat — {lead.destination}</h6>
        <Button size="sm" variant="outline-secondary" onClick={onClose}>Close</Button>
      </div>
      {error && <Alert variant="danger">{error}</Alert>}
      {loading ? (
        <p className="text-muted mb-0">Loading messages…</p>
      ) : (
        <div className="d-flex flex-column gap-2 mb-3" style={{ maxHeight: 240, overflowY: "auto" }}>
          {messages.length === 0 ? (
            <p className="text-muted mb-0">No messages yet. Say hello to the customer.</p>
          ) : (
            messages.map((m) => (
              <div key={m._id} className={`d-flex ${m.senderRole === "Partner" ? "justify-content-end" : "justify-content-start"}`}>
                <div
                  className="px-3 py-2 rounded-3"
                  style={{ maxWidth: "75%", background: m.senderRole === "Partner" ? "#fff3ee" : "#f3f4f6" }}
                >
                  <div>{m.text}</div>
                  <small className="text-muted">{new Date(m.createdAt).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })}</small>
                </div>
              </div>
            ))
          )}
        </div>
      )}
      <Form onSubmit={handleSend} className="d-flex gap-2">
        <Form.Control placeholder="Type a message…" value={text} onChange={(e) => setText(e.target.value)} />
        <Button type="submit" disabled={sending}>{sending ? "Sending…" : "Send"}</Button>
      </Form>
    </div>
  );
}

export default function Requests() {
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [quoteFor, setQuoteFor] = useState(null);
  const [items, setItems] = useState([emptyItem()]);
  const [validUntil, setValidUntil] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState("");
  const [chatFor, setChatFor] = useState(null);

  const load = () => {
    setLoading(true);
    listLeads()
      .then(({ data }) => setLeads(data.data || []))
      .catch((err) => setError(getErr(err, "Failed to load requests")))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const total = items.reduce((sum, it) => sum + (Number(it.amount) || 0), 0);

  const openQuoteForm = (lead) => {
    setQuoteFor(lead);
    setItems([emptyItem()]);
    setValidUntil("");
    setNotes("");
    setSuccess("");
    setError("");
  };

  const handleSubmitQuote = async (e) => {
    e.preventDefault();
    setError(""); setSuccess("");
    const cleanItems = items.filter((it) => it.label && it.amount);
    if (cleanItems.length === 0) return setError("Add at least one priced line item");
    if (!validUntil) return setError("Set a validity date/time for this quote");

    setSubmitting(true);
    try {
      await submitQuote(quoteFor._id, {
        lineItems: cleanItems.map((it) => ({ label: it.label, amount: Number(it.amount) })),
        validUntil,
        notes,
      });
      setSuccess("Quote submitted successfully");
      setQuoteFor(null);
      load();
    } catch (err) {
      setError(getErr(err, "Unable to submit quote. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <p>Loading requests…</p>;

  return (
    <div>
      <h4 className="mb-3">Customer Requests</h4>
      {error && !quoteFor && <Alert variant="danger">{error}</Alert>}
      {success && <Alert variant="success">{success}</Alert>}

      {leads.length === 0 ? (
        <div className="partner-card partner-empty">You don't have any matched requests yet. We'll notify you as relevant customer requests come in.</div>
      ) : (
        <div className="d-flex flex-column gap-3">
          {leads.map((lead) => (
            <div key={lead._id}>
              <div className="partner-card d-flex justify-content-between align-items-center flex-wrap gap-3">
                <div>
                  <h6 className="mb-1">{lead.destination}</h6>
                  <small className="text-muted">
                    {lead.travellersCount} traveller(s) · Budget ₹{lead.budgetMin || 0}–₹{lead.budgetMax || 0}
                    {lead.requirementTags?.length > 0 && ` · ${lead.requirementTags.join(", ")}`}
                  </small>
                  {lead.notes && <p className="mb-0 mt-1 small">{lead.notes}</p>}
                </div>
                <div className="d-flex align-items-center gap-2">
                  <Button size="sm" variant="outline-secondary" onClick={() => setChatFor(chatFor?._id === lead._id ? null : lead)}>
                    {chatFor?._id === lead._id ? "Hide Chat" : "Message"}
                  </Button>
                  {lead.alreadyQuoted ? (
                    <Badge bg="secondary">Quote Submitted</Badge>
                  ) : (
                    <Button size="sm" onClick={() => openQuoteForm(lead)}>Submit Quote</Button>
                  )}
                </div>
              </div>
              {chatFor?._id === lead._id && <ChatPanel lead={lead} onClose={() => setChatFor(null)} />}
            </div>
          ))}
        </div>
      )}

      {quoteFor && (
        <div className="partner-card mt-4">
          <h5>Quote for {quoteFor.destination}</h5>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form onSubmit={handleSubmitQuote}>
            {items.map((item, i) => (
              <div key={i} className="d-flex gap-2 mb-2">
                <Form.Control
                  placeholder="Item (e.g. Hotel)"
                  value={item.label}
                  onChange={(e) => {
                    const next = [...items]; next[i] = { ...next[i], label: e.target.value }; setItems(next);
                  }}
                />
                <Form.Control
                  type="number"
                  placeholder="Amount ₹"
                  style={{ maxWidth: 160 }}
                  value={item.amount}
                  onChange={(e) => {
                    const next = [...items]; next[i] = { ...next[i], amount: e.target.value }; setItems(next);
                  }}
                />
              </div>
            ))}
            <Button variant="link" size="sm" className="px-0 mb-2" onClick={() => setItems([...items, emptyItem()])}>
              + Add line item
            </Button>
            <div className="fw-semibold mb-3">Total: ₹{total}</div>

            <Form.Group className="mb-3">
              <Form.Label>Quote valid until</Form.Label>
              <Form.Control type="datetime-local" value={validUntil} onChange={(e) => setValidUntil(e.target.value)} required />
            </Form.Group>
            <Form.Group className="mb-3">
              <Form.Label>Notes (inclusions/exclusions/terms)</Form.Label>
              <Form.Control as="textarea" rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
            </Form.Group>

            <div className="d-flex gap-2">
              <Button type="submit" disabled={submitting}>{submitting ? "Submitting…" : "Submit Quote"}</Button>
              <Button variant="outline-secondary" onClick={() => setQuoteFor(null)}>Cancel</Button>
            </div>
          </Form>
        </div>
      )}
    </div>
  );
}
